import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, Users, ClipboardList, Tag as TagIcon, Send, ArrowRight, X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { StatsAPI } from '../api';
import FeedbackDialog from './FeedbackDialog';

const DISMISS_KEY = 'shiftnotes_onboarding_dismissed_v1';

export default function OnboardingChecklist() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [shared, setShared] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackShown, setFeedbackShown] = useState(false);

  useEffect(() => {
    setDismissed(localStorage.getItem(DISMISS_KEY) === '1');
    setShared(localStorage.getItem('shiftnotes_shared_feedback') === '1');
    setFeedbackShown(localStorage.getItem('shiftnotes_feedback_prompted') === '1');
    StatsAPI.team().then(setStats).catch(() => setStats(null));
  }, []);

  const allComplete = stats && (stats.players || 0) > 0 && (stats.evaluations || 0) > 0 && (stats.tags || 0) > 0 && shared;
  useEffect(() => {
    if (allComplete && !feedbackShown) {
      setFeedbackOpen(true);
      setFeedbackShown(true);
      localStorage.setItem('shiftnotes_feedback_prompted', '1');
    }
  }, [allComplete, feedbackShown]);

  if (!stats || dismissed) return null;

  const steps = [
    {
      id: 'player', label: 'Add a player', desc: 'Bring an athlete into your roster.',
      icon: Users, done: (stats.players || 0) > 0, cta: 'Go to Players',
      onClick: () => navigate('/app/players'),
    },
    {
      id: 'evaluation', label: 'Complete an evaluation', desc: 'Rate the athlete across the development rubric.',
      icon: ClipboardList, done: (stats.evaluations || 0) > 0, cta: 'Start Evaluation',
      onClick: () => navigate('/app/evaluate/new'),
    },
    {
      id: 'tag', label: 'Tag a video moment', desc: 'Give the feedback a play-by-play receipt.',
      icon: TagIcon, done: (stats.tags || 0) > 0, cta: 'Open Video Library',
      onClick: () => navigate('/app/videos'),
    },
    {
      id: 'share', label: 'Share feedback with the athlete', desc: 'Open a report or the family view to close the loop.',
      icon: Send, done: shared, cta: 'Open Reports',
      onClick: () => {
        localStorage.setItem('shiftnotes_shared_feedback', '1');
        setShared(true);
        navigate('/app/reports');
      },
    },
  ];

  const doneCount = steps.filter(s => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);
  const allDone = doneCount === steps.length;
  const nextStep = steps.find(s => !s.done);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div className="relative bg-white border border-slate-200 rounded-xl p-6 mb-6 shadow-sm">
      <button onClick={dismiss} aria-label="Dismiss checklist"
        className="absolute top-3 right-3 text-slate-400 hover:text-slate-700">
        <X className="h-4 w-4" />
      </button>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold">Getting Started</div>
          <h3 className="font-display text-lg font-semibold text-slate-900 mt-0.5">
            {allDone ? "You're all set. Nice work." : 'The 4-step ShiftNotes loop'}
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            {allDone
              ? 'Every part of the development loop is now live in your program.'
              : 'Add Player  Complete Evaluation  Tag a Clip  Share Feedback'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xs text-slate-500">{doneCount} of {steps.length} complete</div>
            <div className="w-40 h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-sky-500 to-cyan-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
          {nextStep && (
            <Button onClick={nextStep.onClick} className="bg-sky-500 hover:bg-sky-600 text-white font-semibold">
              {nextStep.cta} <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <button key={s.id} onClick={s.onClick} disabled={s.done}
              className={`text-left flex items-start gap-3 p-3.5 rounded-lg border transition-colors ${
                s.done
                  ? 'border-emerald-500/30 bg-emerald-500/5 cursor-default'
                  : 'border-slate-200 bg-slate-50 hover:border-sky-500/40 hover:bg-white'
              }`}>
              <div className="flex-shrink-0 mt-0.5">
                {s.done
                  ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  : <Circle className="h-5 w-5 text-slate-300" />}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-[10px] text-sky-600">{String(i + 1).padStart(2, '0')}</span>
                  <Icon className="h-3.5 w-3.5 text-slate-500" />
                  <span className={`font-semibold text-sm ${s.done ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                    {s.label}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-1 leading-snug">{s.desc}</div>
              </div>
            </button>
          );
        })}
      </div>
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </div>
  );
}
